
import React from 'react';
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { EditProfile } from "@/components/EditProfile";
import { 
  MapPin, 
  Globe, 
  Camera,
  LogOut,
  UserPlus,
  UserMinus,
  Pencil,
  User,
  Link,
  ChevronUp,
  ChevronDown
} from "lucide-react";

type SocialMediaPlatform = "instagram" | "twitter" | "facebook" | "youtube" | "website";
interface SocialMediaLink {
  platform: SocialMediaPlatform;
  url: string;
}

interface ProfileHeaderDesktopProps {
  profile: {
    id: string;
    username?: string;
    avatar_url?: string;
    banner_url?: string;
    bio?: string;
    website?: string;
    location?: string;
    social_links?: SocialMediaLink[];
  };
  currentUser: string | null;
  isFollowing: boolean;
  followersCount: number;
  followingCount: number;
  onProfileUpdate: () => void;
  onFollowToggle: () => void;
  onLogout: () => void;
  handleBannerFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleAvatarFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  bannerFileRef: React.RefObject<HTMLInputElement>;
  avatarFileRef: React.RefObject<HTMLInputElement>;
  isOwnProfile: boolean;
  bannerVisible: boolean;
  setBannerVisible: (visible: boolean) => void;
}

export function ProfileHeaderDesktop({
  profile,
  currentUser,
  isFollowing,
  followersCount,
  followingCount,
  onProfileUpdate,
  onFollowToggle,
  onLogout,
  handleBannerFileChange,
  handleAvatarFileChange,
  bannerFileRef,
  avatarFileRef,
  isOwnProfile,
  bannerVisible,
  setBannerVisible
}: ProfileHeaderDesktopProps) {
  const [isEditProfileOpen, setIsEditProfileOpen] = React.useState(false);

  return (
    <>
      <div className="relative w-auto mt-12">
        {/* Banner Toggle Button */}
        <Button 
          onClick={() => setBannerVisible(!bannerVisible)}
          variant="outline" 
          size="sm"
          className="absolute right-4 top-2 z-10"
        >
          {bannerVisible ? (
            <>
              <ChevronUp className="h-4 w-4 mr-1" />
              Ocultar Banner
            </>
          ) : (
            <>
              <ChevronDown className="h-4 w-4 mr-1" />
              Mostrar Banner
            </>
          )}
        </Button>

        {/* Banner - conditionally rendered based on visibility state */}
        {bannerVisible && (
          <div 
            className="h-72 w-full bg-muted overflow-hidden relative transition-all duration-300"
            style={{
              backgroundImage: profile.banner_url ? `url(${profile.banner_url})` : undefined,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          >
            {isOwnProfile && (
              <Button 
                onClick={() => bannerFileRef.current?.click()}
                variant="secondary" 
                size="icon"
                className="absolute bottom-4 right-4 bg-background/80 hover:bg-background/90"
              >
                <Camera className="h-4 w-4" />
                <input
                  type="file"
                  ref={bannerFileRef}
                  onChange={handleBannerFileChange}
                  accept="image/*"
                  className="hidden"
                />
              </Button>
            )}
          </div>
        )}
        
        {/* Profile Info - Adjust positioning based on banner visibility */}
        <div className={`max-w-4xl mx-auto px-4 relative ${bannerVisible ? '-mt-12' : 'pt-12'}`}>
          <div className="flex items-end justify-between">
            <div className="flex items-end gap-6">
              {/* Avatar */}
              <div className="relative">
                <Avatar className="h-32 w-32 border-4 border-background">
                  <AvatarImage src={profile.avatar_url || ""} />
                  <AvatarFallback className="text-4xl">
                    {profile.username?.charAt(0).toUpperCase() || "U"}
                  </AvatarFallback>
                </Avatar>
                {isOwnProfile && (
                  <Button 
                    onClick={() => avatarFileRef.current?.click()}
                    variant="secondary" 
                    size="icon"
                    className="absolute bottom-0 right-0 rounded-full"
                  >
                    <Camera className="h-4 w-4" />
                    <input
                      type="file"
                      ref={avatarFileRef}
                      onChange={handleAvatarFileChange}
                      accept="image/*"
                      className="hidden"
                    />
                  </Button>
                )}
              </div>
              
              <div className="pb-4">
                <h1 className="text-2xl font-bold mt-2">{profile.username}</h1>
                <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                  <span><strong>{followersCount}</strong> seguidores</span>
                  <span><strong>{followingCount}</strong> seguindo</span>
                </div>
              </div>
            </div>
            
            <div className="pb-4 flex gap-2">
              {isOwnProfile ? (
                <>
                  <Button variant="outline" onClick={onLogout}>
                    <LogOut className="h-4 w-4 mr-2" />
                    Sair
                  </Button>
                  <Button onClick={() => setIsEditProfileOpen(true)}>
                    <Pencil className="h-4 w-4 mr-2" />
                    Editar Perfil
                  </Button>
                </>
              ) : (
                <Button 
                  onClick={onFollowToggle} 
                  variant={isFollowing ? "secondary" : "default"}
                >
                  {isFollowing ? (
                    <>
                      <UserMinus className="h-4 w-4 mr-2" />
                      Deixar de seguir
                    </>
                  ) : (
                    <>
                      <UserPlus className="h-4 w-4 mr-2" />
                      Seguir
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
          
          {/* Bio and other info */}
          <div className="mt-6 max-w-2xl space-y-4">
            {profile.bio && (
              <div className="space-y-1">
                <h3 className="text-sm font-medium flex items-center gap-1">
                  <User className="h-4 w-4" /> 
                  Biografia
                </h3>
                <p className="text-sm">{profile.bio}</p>
              </div>
            )}
            
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              {profile.location && (
                <div className="space-y-1">
                  <h3 className="text-sm font-medium flex items-center gap-1">
                    <MapPin className="h-4 w-4" /> 
                    Localização
                  </h3>
                  <p className="text-sm text-muted-foreground">{profile.location}</p>
                </div>
              )}
              
              {profile.website && (
                <div className="space-y-1">
                  <h3 className="text-sm font-medium flex items-center gap-1">
                    <Globe className="h-4 w-4" /> 
                    Website
                  </h3>
                  <a 
                    href={profile.website.startsWith('http') ? profile.website : `https://${profile.website}`}
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sm text-primary hover:underline"
                  >
                    {profile.website}
                  </a>
                </div>
              )}

              {profile.social_links && profile.social_links.length > 0 && (
                <div className="space-y-1">
                  <h3 className="text-sm font-medium flex items-center gap-1">
                    <Link className="h-4 w-4" /> 
                    Redes Sociais
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {profile.social_links.map((link, index) => (
                      <a 
                        key={index}
                        href={link.url.startsWith("http") ? link.url : `https://${link.url}`}
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-primary hover:underline mr-2"
                      >
                        {link.platform.charAt(0).toUpperCase() + link.platform.slice(1)}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      <EditProfile 
        isOpen={isEditProfileOpen} 
        onOpenChange={setIsEditProfileOpen} 
        onProfileUpdate={onProfileUpdate} 
      />
    </>
  );
}
